import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * HEXA Buddy — floating AI assistant widget on the marketing site.
 * Public / unauthenticated. Powered by Claude Sonnet via /api/public/hexa-buddy.
 * Renders as a floating pill in the bottom-right that expands into a chat sheet.
 */
export default function HexaBuddy({ open: openProp, onClose, hideLauncher = false } = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (v) => {
    if (isControlled) {
      if (!v && typeof onClose === "function") onClose();
    } else {
      setInternalOpen(typeof v === "function" ? v(internalOpen) : v);
    }
  };
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm HEXA Buddy, your guide to HEXA OS. Ask me anything about features, pricing, or how to get started. ✨",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setBusy(true);
    try {
      const history = nextMessages.slice(-8, -1); // last few turns before this one
      const r = await axios.post(`${API_URL}/api/public/hexa-buddy`, {
        message: text,
        session_id: sessionId,
        history,
      });
      const reply = (r.data?.reply || "").trim();
      if (r.data?.session_id && !sessionId) setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: reply || "Hmm, I'm not sure — could you rephrase?" }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "network error";
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `I'm having trouble reaching my brain right now (${detail}). Please try again in a moment.` },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "What can HEXA OS do?",
    "How much does Pro cost?",
    "Is there a free plan?",
    "How do I invite my team?",
  ];

  // Rendered through a portal into document.body on purpose. The OS top bar
  // (.cmdstrip) sets backdrop-filter, which makes it the containing block for
  // position:fixed descendants — mounting inline there resolved this panel
  // against a 28px-tall strip and pushed it ~612px above the viewport.
  return createPortal(
    <>
      {/* Floating launcher */}
      {!hideLauncher && (
      <button
        data-testid="hexa-buddy-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close HEXA Buddy" : "Open HEXA Buddy"}
        className="fixed z-[1500] rounded-full transition-transform hover:scale-105"
        style={{
          bottom: 24,
          right: 24,
          width: 60,
          height: 60,
          background: "linear-gradient(135deg, #8b5cf6, #38bdf8, #22d3ee)",
          boxShadow: "0 10px 30px rgba(139,92,246,0.45), 0 4px 12px rgba(15,23,42,0.35)",
          border: "2px solid rgba(255,255,255,0.35)",
        }}
      >
        <div className="w-full h-full grid place-items-center text-white">
          {open ? <X size={24} strokeWidth={2.2} /> : <MessageCircle size={24} strokeWidth={2.2} />}
        </div>
        {!open && (
          <span
            className="absolute rounded-full"
            style={{
              width: 12, height: 12, top: -2, right: -2,
              background: "#fbbf24",
              border: "2px solid #ffffff",
              boxShadow: "0 0 8px rgba(251,191,36,0.7)",
            }}
          />
        )}
      </button>
      )}

      {/* Chat sheet */}
      {open && (
        <div
          data-testid="hexa-buddy-panel"
          className="fixed z-[1500] flex flex-col overflow-hidden"
          style={{
            bottom: 100,
            right: 24,
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            height: 540,
            maxHeight: "calc(100vh - 140px)",
            background: "#ffffff",
            borderRadius: 18,
            border: "1px solid rgba(139,92,246,0.25)",
            boxShadow: "0 24px 60px rgba(15,23,42,0.35), 0 0 0 1px rgba(139,92,246,0.08)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-4 py-3"
            style={{
              background: "linear-gradient(120deg, #8b5cf6, #38bdf8)",
              color: "#ffffff",
            }}
          >
            <div
              className="grid place-items-center rounded-full"
              style={{
                width: 34, height: 34,
                background: "rgba(255,255,255,0.22)",
                border: "1px solid rgba(255,255,255,0.4)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold leading-tight">HEXA Buddy</div>
              <div className="text-[11px] text-white/85">Your AI guide to HEXA OS</div>
            </div>
            <button
              data-testid="hexa-buddy-close"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/15"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            data-testid="hexa-buddy-messages"
            className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5"
            style={{ background: "#f8fafc" }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`hexa-buddy-msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-snug whitespace-pre-wrap"
                  style={
                    m.role === "user"
                      ? {
                          background: "linear-gradient(120deg, #8b5cf6, #6366f1)",
                          color: "#ffffff",
                          borderBottomRightRadius: 6,
                        }
                      : {
                          background: "#ffffff",
                          color: "#0f172a",
                          border: "1px solid #e2e8f0",
                          borderBottomLeftRadius: 6,
                        }
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div
                  className="px-3 py-2 rounded-2xl text-[13px] flex items-center gap-2"
                  style={{
                    background: "#ffffff",
                    color: "#64748b",
                    border: "1px solid #e2e8f0",
                    borderBottomLeftRadius: 6,
                  }}
                >
                  <Loader2 size={12} className="animate-spin" /> HEXA Buddy is thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 2 && !busy && (
            <div className="px-3 pb-2 pt-1 flex flex-wrap gap-1.5" style={{ background: "#f8fafc" }}>
              {suggestions.map((s) => (
                <button
                  key={s}
                  data-testid={`hexa-buddy-suggest-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                  onClick={() => setInput(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full transition"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #cbd5e1",
                    color: "#475569",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={send}
            className="flex items-center gap-2 px-3 py-2.5"
            style={{ background: "#ffffff", borderTop: "1px solid #e2e8f0" }}
          >
            <input
              data-testid="hexa-buddy-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask HEXA Buddy anything…"
              maxLength={800}
              disabled={busy}
              className="flex-1 outline-none bg-transparent text-[13px] text-slate-900 placeholder-slate-400"
            />
            <button
              type="submit"
              data-testid="hexa-buddy-send"
              disabled={busy || !input.trim()}
              className="p-2 rounded-full disabled:opacity-40 transition"
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #38bdf8)",
                color: "#ffffff",
              }}
              aria-label="Send"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        </div>
      )}
    </>,
    document.body
  );
}
